import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Col, Row, Form } from "react-bootstrap";
import { Autocomplete } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import CancelButton from "Common/Components/Buttons/CancelButton";
import PrimarySubmitButton from "Common/Components/Buttons/PrimarySubmitButton";
import PageBreadcrumb from "Common/Components/PageBreadcrumb/PageBreadcrumb";
import PageContainer from "Common/Components/PageContainer/PageContainer";
import BootstrapTextField from "Common/Components/InputComponent/BootstrapTextField";
import { CURRENCY_LIST } from "assets/CurrencyListJson";
import {
  addCurrency,
  fetchCurrencyById,
  updateCurrency,
} from "slices/Masters/currency/thunk";
import LoadingSpinner from "Common/Components/LoaderErrorWrapper/LoadingSpinner";
import { clearTableState } from "helpers/ClearTableState/ClearTableState";
import { TableKeys } from "Constants/TableKeys/TableKeys";
import { useScrollToFormikError } from "hooks/useScrollToFormikError";
import { autocompleteDropdownStyles } from "Constants/Style";

interface CurrencyOption {
  code: string;
  name: string;
  symbol: string;
}

const CurrencyForm: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const isEdit = Boolean(id);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const [initialData, setInitialData] = useState({
    symbol: "",
    currency_name: "",
    currency_code: "",
  });

  // Fetch data on edit
  useEffect(() => {
    if (isEdit && id) {
      setIsLoading(true);
      dispatch(fetchCurrencyById(id))
        .unwrap()
        .then((data: any) => {
          data = data.data || data;
          setInitialData({
            symbol: data.currency_symbol || "",
            currency_name: data.currency_name || "",
            currency_code: data.currency_code || "",
          });
        })
        .catch(() => toast.error("Failed to fetch Currency details"))
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [id]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      symbol: initialData.symbol || "",
      currency_name: initialData.currency_name || "",
      currency_code: initialData.currency_code || "",
    },
    validationSchema: Yup.object({
      currency_name: Yup.string().trim().required("Please enter Currency Name"),

      // .matches(/^[A-Za-z\s]+$/, "Currency Name must contain only letters and spaces"),
    }),
    onSubmit: async (values) => {
      const payload = {
        currency_name: values.currency_name.trim(),
        currency_symbol: values.symbol.trim(),
        currency_code: values.currency_code.trim(),
      };

      setIsSubmitting(true);
      try {
        if (isEdit) {
          await dispatch(updateCurrency({ id, ...payload })).unwrap();
          toast.success("Currency updated successfully");
        } else {
          await dispatch(addCurrency(payload)).unwrap();
          clearTableState(TableKeys.MASTER.CURRENCY);
          toast.success("Currency added successfully");
        }
        navigate("/master/currency");
      } catch (error: any) {
        toast.error(error.message || "Something went wrong");
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  useScrollToFormikError(
    formik.errors,
    formik.submitCount,
    formik.isSubmitting,
  );

  const selectedCurrency = useMemo(() => {
    return (
      CURRENCY_LIST.find(
        (c) =>
          c.code === formik.values.currency_code ||
          c.name === formik.values.currency_name
      ) || null
    );
  }, [formik.values.currency_code, formik.values.currency_name]);

  const handleCurrencySelect = (currency: CurrencyOption | null) => {
    if (currency) {
      const newValues = {
        ...formik.values,
        currency_name: currency.name,
        currency_code: currency.code,
        symbol: currency.symbol,
      };
      formik.setValues(newValues);
      formik.setTouched({
        ...formik.touched,
        currency_name: true,
        currency_code: true,
        symbol: true,
      });
      // Defer validation to next tick so it runs after React commits the state update
      setTimeout(() => {
        formik.validateForm();
      }, 0);
    } else {
      formik.setValues(
        {
          ...formik.values,
          currency_name: "",
          currency_code: "",
          symbol: "",
        },
        true
      );
    }
  };

  return (
    <PageContainer>
      <PageBreadcrumb
        title={id ? "Edit Currency" : "Add Currency"}
        titleLink="/master/currency"
        breadcrumbs={[
          { label: "Currency", to: "/master/currency" },
          { label: id ? "Edit Currency" : "Add Currency", to: "" },
        ]}
        backNavigationPath={-1}
      />

      <hr />
      {isLoading && isEdit ? (
        <LoadingSpinner />
      ) : (
        <Form noValidate autoComplete="off" onSubmit={formik.handleSubmit}>
          <Row className="mb-3">
            <Col xs={12} md={4}>
              <Form.Label className="fw-semibold">
                Currency Name <span className="text-danger">*</span>
              </Form.Label>
              <Autocomplete
                options={CURRENCY_LIST}
                getOptionLabel={(option) =>
                  typeof option === "string" ? option : option.name
                }
                isOptionEqualToValue={(option, value) =>
                  option.code === value?.code && option.name === value?.name
                }
                value={selectedCurrency}
                onChange={(e, newValue) => handleCurrencySelect(newValue)}
                ListboxProps={autocompleteDropdownStyles.ListboxProps}
                renderOption={(props, option) =>
                  autocompleteDropdownStyles.renderOption(
                    props,
                    option,
                    (opt) => (typeof opt === "string" ? opt : opt?.name) || ""
                  )
                }
                renderInput={(params) => (
                  <BootstrapTextField
                    {...params}
                    name="currency_name"
                    placeholder="Select Currency Name"
                    size="small"
                    error={
                      !!formik.errors.currency_name &&
                      formik.touched.currency_name
                    }
                    helperText={
                      formik.touched.currency_name &&
                      formik.errors.currency_name
                    }
                    fullWidth
                    sx={autocompleteDropdownStyles.inputSx}
                  />
                )}
              />
            </Col>
            <Col xs={12} md={4}>
              <Form.Label className="fw-semibold">
                Currency Code <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="currency_code"
                placeholder="Enter Currency Code"
                value={formik.values.currency_code}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled
                readOnly
                isInvalid={
                  !!formik.errors.currency_code && formik.touched.currency_code
                }
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.currency_code}
              </Form.Control.Feedback>
            </Col>
            <Col xs={12} md={4}>
              <Form.Label className="fw-semibold">
                Symbol <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="symbol"
                placeholder="Enter symbol"
                value={formik.values.symbol}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled
                readOnly
                isInvalid={!!formik.errors.symbol && formik.touched.symbol}
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.symbol}
              </Form.Control.Feedback>
            </Col>
          </Row>

          {/* Buttons */}
          <div className="d-flex flex-column flex-md-row justify-content-center gap-3 mt-3">
            <CancelButton
              redirectTo="/master/currency"
              disabled={isSubmitting}
            />
            <PrimarySubmitButton
              label={id ? "Update" : "Submit"}
              isLoading={isSubmitting}
              loadingLabel={id ? "Updating..." : "Submitting..."}
            />
          </div>
        </Form>
      )}
    </PageContainer>
  );
};

export default CurrencyForm;
